// Left-rail navigation for the project workspace (CMS expansion, Part 1).
// Replaces the overflowing top tab bar on desktop — the workspace has 13 tabs
// today and five more CMS modules coming. Groups per the design handoff;
// group captions run DARKER than the mock (--ew-n-700 semibold) per the product owner —
// the design file's muted captions were too hard to see at a glance.
//
// All five CMS modules (Materials, Documents, Drawings, Change Mgmt, QA/QC)
// have since shipped, so nothing in RAIL_GROUPS currently sets `disabled`. The
// grayed-out "Soon" rendering path below is retained for the next unbuilt tab:
// set `disabled: true` on its entry here, then add its renderer in
// ProjectWorkspace when it ships.
//
// Purely presentational: tab state stays URL-driven (?tab=) in the workspace;
// the rail just calls onSelect. Collapse is click-pinned (no hover-reveal)
// and persisted by the parent.

interface RailItem {
  key: string;
  label: string;
  /** Not built yet — gray, non-clickable, "Soon" chip. */
  disabled?: boolean;
}

interface RailGroup {
  /** Uppercase caption; null = ungrouped (Overview). */
  caption: string | null;
  items: RailItem[];
}

const RAIL_GROUPS: RailGroup[] = [
  { caption: null, items: [{ key: 'overview', label: 'Overview' }, { key: 'weekly', label: 'Weekly Report' }, { key: 'contacts', label: 'Contacts' }] },
  {
    caption: 'Field',
    items: [
      { key: 'daily',        label: 'Daily Logs' },
      { key: 'mobilization', label: 'Mobilization' },
      { key: 'schedule',     label: 'Schedule' },
      { key: 'manpower',     label: 'Manpower' },
      { key: 'equipment',    label: 'Equipment' },
      { key: 'qaqc',         label: 'QA / QC' },
      { key: 'punch',        label: 'Punch List' },
      { key: 'survey',       label: 'Survey' },
    ],
  },
  {
    caption: 'Procurement',
    items: [
      { key: 'materials', label: 'Materials' },
      { key: 'receiving', label: 'Receiving' },
      { key: 'shipping',  label: 'Shipping' },
    ],
  },
  {
    caption: 'Documents',
    items: [
      { key: 'documents',  label: 'Documents' },
      // Agendas and minutes (#644).
      { key: 'meetings',   label: 'Meetings' },
      // The site photo feed — the same one as Daily Logs ▸ Photos (#665).
      { key: 'photos',     label: 'Photos' },
      { key: 'drawings',   label: 'Drawings' },
      { key: 'submittals', label: 'Submittals' },
      { key: 'rfis',       label: 'RFIs' },
    ],
  },
  {
    caption: 'Controls',
    items: [
      { key: 'changes',     label: 'Change Mgmt' },
      { key: 'safety',      label: 'Safety' },
      { key: 'financials',  label: 'Financials' },
      { key: 'commitment',  label: 'Commitments' },
      { key: 'contracts',   label: 'Contracts' },
      { key: 'reports',     label: 'Reports' },
    ],
  },
];

const css = `
  .pnr {
    flex: 0 0 auto; width: 230px; background: var(--ew-white);
    border-right: 1px solid var(--ew-n-200);
    transition: width var(--ew-dur-med) var(--ew-ease);
    overflow: hidden; align-self: stretch;
  }
  .pnr.collapsed { width: 46px; }
  .pnr-inner { width: 230px; padding: 10px 0 24px; }
  /* Collapsed: the inner shrinks with the rail so the expand toggle stays in
     view (a fixed 230px inner clipped it outside the 46px strip — the rail
     couldn't be reopened). */
  .pnr.collapsed .pnr-inner { width: 46px; }
  .pnr-toggle {
    display: flex; align-items: center; justify-content: center;
    width: 28px; height: 28px; margin: 2px 10px 6px auto;
    border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md);
    background: var(--ew-white); color: var(--ew-n-500); cursor: pointer;
    font-size: 13px; line-height: 1;
  }
  .pnr-toggle:hover { background: var(--ew-n-50); color: var(--ew-dark); }
  .pnr-toggle:focus-visible { outline: 2px solid var(--ew-green); outline-offset: 1px; }
  .pnr.collapsed .pnr-toggle { margin: 2px auto 6px; }
  .pnr-job {
    padding: 4px 16px 8px; font: var(--ew-fw-semibold) 11px/1 var(--ew-font);
    letter-spacing: var(--ew-ls-caption); text-transform: uppercase; color: var(--ew-n-500);
    white-space: nowrap;
  }
  /* Group captions must read at a glance (product owner, twice): brand-green, bold,
     with a divider above each group and items indented beneath — the caption
     sits proud of its list instead of blending into it. */
  .pnr-group { border-top: 1px solid var(--ew-n-100); margin-top: 10px; padding-top: 4px; }
  /* First group = the ungrouped Overview: no divider, no caption indent. */
  .pnr-group:first-of-type { border-top: 0; margin-top: 0; padding-top: 0; }
  .pnr-group:first-of-type .pnr-item { padding-left: 16px; }
  .pnr-caption {
    padding: 10px 16px 6px; font: 700 11.5px/1 var(--ew-font);
    letter-spacing: var(--ew-ls-caption); text-transform: uppercase;
    color: var(--ew-green-700);
    white-space: nowrap;
  }
  .pnr-item {
    display: flex; align-items: center; gap: 8px; width: 100%;
    padding: 7px 16px 7px 24px; border: 0; background: none; text-align: left;
    font: var(--ew-fw-medium) var(--ew-fs-body-sm)/1.3 var(--ew-font);
    color: var(--ew-n-700); cursor: pointer; white-space: nowrap;
    border-left: 3px solid transparent;
    transition: background var(--ew-dur-fast) var(--ew-ease);
  }
  .pnr-item:hover { background: var(--ew-n-50); }
  .pnr-item:focus-visible { outline: 2px solid var(--ew-green); outline-offset: -2px; }
  .pnr-item.active {
    background: var(--ew-green-50); color: var(--ew-green-900);
    font-weight: var(--ew-fw-semibold); border-left-color: var(--ew-green);
  }
  .pnr-item.disabled { color: var(--ew-n-300); cursor: default; }
  .pnr-item.disabled:hover { background: none; }
  .pnr-count {
    margin-left: auto; padding: 1px 7px; border-radius: var(--ew-r-pill);
    background: var(--ew-n-100); color: var(--ew-n-600);
    font: var(--ew-fw-semibold) 11px/1.6 var(--ew-font); font-variant-numeric: tabular-nums;
  }
  .pnr-item.active .pnr-count { background: var(--ew-white); color: var(--ew-green-900); }
  .pnr-soon {
    margin-left: auto; padding: 1px 6px; border-radius: var(--ew-r-sm);
    font: var(--ew-fw-semibold) 9px/1.6 var(--ew-font);
    letter-spacing: 0.05em; text-transform: uppercase;
    background: var(--ew-n-50); color: var(--ew-n-400); border: 1px solid var(--ew-n-200);
  }
`;

export default function ProjectNavRail({
  jobNumber, activeTab, counts, onSelect, collapsed, onToggle, hiddenKeys,
}: {
  jobNumber: string;
  activeTab: string;
  counts: Record<string, number | undefined>;
  onSelect: (key: string) => void;
  collapsed: boolean;
  onToggle: () => void;
  /** Tab keys the current user may not see (e.g. office tabs for
   *  field-leader-only users) — mirrors the mobile tab bar's gating. */
  hiddenKeys?: ReadonlySet<string>;
}) {
  // Filter gated items; drop a group when nothing clickable remains (a lone
  // "Soon" placeholder under an otherwise-hidden caption is just noise).
  const groups = RAIL_GROUPS
    .map((g) => ({ ...g, items: g.items.filter((i) => !hiddenKeys?.has(i.key)) }))
    .filter((g) => g.items.some((i) => !i.disabled));
  return (
    <nav aria-label="Project sections" className={`pnr pw-rail${collapsed ? ' collapsed' : ''}`}>
      <style>{css}</style>
      <div className="pnr-inner">
        <button
          type="button"
          className="pnr-toggle"
          onClick={onToggle}
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          title={collapsed ? 'Expand navigation' : 'Collapse navigation'}
        >
          {collapsed ? '»' : '«'}
        </button>

        {!collapsed && (
          <>
            <div className="pnr-job">Job {jobNumber}</div>
            {groups.map((group, gi) => (
              <div key={gi} className="pnr-group">
                {group.caption && <div className="pnr-caption">{group.caption}</div>}
                {group.items.map((item) => {
                  const n = counts[item.key];
                  if (item.disabled) {
                    return (
                      <div
                        key={item.key}
                        className="pnr-item disabled"
                        aria-disabled="true"
                        title="Coming soon — this module is being built"
                      >
                        {item.label}
                        <span className="pnr-soon">Soon</span>
                      </div>
                    );
                  }
                  const active = item.key === activeTab;
                  return (
                    <button
                      key={item.key}
                      type="button"
                      className={`pnr-item${active ? ' active' : ''}`}
                      aria-current={active ? 'page' : undefined}
                      onClick={() => onSelect(item.key)}
                    >
                      {item.label}
                      {typeof n === 'number' && n > 0 ? <span className="pnr-count">{n}</span> : null}
                    </button>
                  );
                })}
              </div>
            ))}
          </>
        )}
      </div>
    </nav>
  );
}
